/**
 * Advanced editor FX — shell-only mapping from a resolved `look` selection to an
 * animated preview overlay (rain / lightning / radial focus blur) and to the
 * on-subject anchor for wearable prop stickers.
 *
 * These reuse existing `look` / `sticker` op kinds and the SourcePack schema —
 * nothing here enters `@desk/core`, scoring, or `validateSourcePack`. The catalog
 * entries in `creative-catalogs.ts` carry the real (near-neutral) grade knobs;
 * the shell recognizes their ids/tags below to render the atmospheric layer.
 */

export const FX_LOOK_FOCUS_BLUR = "look-focus-blur";
export const FX_LOOK_RAIN = "look-rain";
export const FX_LOOK_LIGHTNING = "look-lightning";

export type FxKind = "focus-blur" | "rain" | "lightning";

/**
 * A stackable EFFECT (rain / lightning / focus blur). Unlike a FILTER (the single
 * color grade held by the core `look` op), effects are game-side PRESENTATION
 * state: several can be active at once and they never enter the scored edit graph
 * or `@desk/core`. Their emotion contribution (if any) comes from the scenario's
 * `LandingConfig.levers.effects` map — some are purely cosmetic (zero emotion).
 *
 * `id` keeps the historic `look-*` id so ownership (free cosmetic set) and the
 * static export approximation (which still reads a `look` op) stay compatible.
 */
export interface CreativeEffectDef {
  id: string;
  fx: FxKind;
  label: string;
  /** One-line description shown under the toggle tile. */
  blurb: string;
}

/** The stackable effects, in the order the EFFECTS row renders them. */
export const CREATIVE_EFFECTS: readonly CreativeEffectDef[] = [
  { id: FX_LOOK_RAIN, fx: "rain", label: "Rain", blurb: "Moody storm streaks" },
  { id: FX_LOOK_LIGHTNING, fx: "lightning", label: "Lightning", blurb: "Dramatic strikes" },
  { id: FX_LOOK_FOCUS_BLUR, fx: "focus-blur", label: "Focus Blur", blurb: "Blur the surround" },
];

/** Effect ids that are stackable effects (NOT single-select filter grades). */
export const EFFECT_LOOK_IDS: ReadonlySet<string> = new Set(
  CREATIVE_EFFECTS.map((e) => e.id),
);

/**
 * A PLACED effect: an effect id active during one story beat's window. Effects
 * are now scrub-placed and snap to per-beat windows (like sticker zones), so the
 * same effect can be dropped on several beats and several effects can share a
 * beat. Still game-side presentation only — never enters `@desk/core`.
 */
export interface EffectInstance {
  effectId: string;
  beatId: string;
}

/** Stable key for an effect-on-a-beat placement (dedupe / list keys). */
export function effectInstanceKey(inst: EffectInstance): string {
  return `${inst.effectId}@${inst.beatId}`;
}

export function hasEffectInstance(
  list: readonly EffectInstance[],
  effectId: string,
  beatId: string,
): boolean {
  return list.some((i) => i.effectId === effectId && i.beatId === beatId);
}

/**
 * Toggle one effect on one beat (add if absent, remove if present), returning a
 * NEW array. Independent + stackable: touching one placement never disturbs the
 * others, so any combination on any beats persists. Pure + order-insensitive.
 */
export function toggleEffectInstance(
  list: readonly EffectInstance[],
  effectId: string,
  beatId: string,
): EffectInstance[] {
  return hasEffectInstance(list, effectId, beatId)
    ? list.filter((i) => !(i.effectId === effectId && i.beatId === beatId))
    : [...list, { effectId, beatId }];
}

export function removeEffectInstance(
  list: readonly EffectInstance[],
  effectId: string,
  beatId: string,
): EffectInstance[] {
  return list.filter((i) => !(i.effectId === effectId && i.beatId === beatId));
}

/** Effect ids active on a given beat (order preserved). */
export function effectIdsAtBeat(list: readonly EffectInstance[], beatId: string): string[] {
  const out: string[] = [];
  for (const i of list) if (i.beatId === beatId && !out.includes(i.effectId)) out.push(i.effectId);
  return out;
}

/** All distinct effect ids placed on ANY beat (presence-based: emotion + export). */
export function distinctEffectIds(list: readonly EffectInstance[]): string[] {
  const out: string[] = [];
  for (const i of list) if (!out.includes(i.effectId)) out.push(i.effectId);
  return out;
}

/** True when a treatment id is a stackable EFFECT rather than a FILTER grade. */
export function isEffectLook(treatmentId: string | null | undefined): boolean {
  return treatmentId != null && EFFECT_LOOK_IDS.has(treatmentId);
}

/** The FxKind for an effect id, or null when the id is not a known effect. */
export function fxKindForEffectId(id: string): FxKind | null {
  return CREATIVE_EFFECTS.find((e) => e.id === id)?.fx ?? null;
}

/** Map a set of active effect ids to their FxKinds (unknown ids dropped). */
export function fxKindsForEffectIds(ids: readonly string[]): FxKind[] {
  const out: FxKind[] = [];
  for (const id of ids) {
    const fx = fxKindForEffectId(id);
    if (fx && !out.includes(fx)) out.push(fx);
  }
  return out;
}

/** Resolve which advanced FX overlay a look selection drives, if any. */
export function fxKindForLook(
  treatmentId: string | null | undefined,
  tags: readonly string[] = [],
): FxKind | null {
  if (!treatmentId) return null;
  if (treatmentId === FX_LOOK_FOCUS_BLUR || tags.includes("focus")) return "focus-blur";
  if (treatmentId === FX_LOOK_RAIN || tags.includes("rain")) return "rain";
  if (treatmentId === FX_LOOK_LIGHTNING || tags.includes("lightning")) return "lightning";
  return null;
}

/** Sticker tag marking a prop that should be anchored on the subject (the cat). */
export const ON_SUBJECT_TAG = "on-subject";

/**
 * Preset per-prop anchor over the cat (normalized millipoints; 500 = 50% x,
 * 580 = 58% y). The cat's face sits around the upper-middle of the frame — shades
 * ride on the EYES (~58% y), hats/crown ride ABOVE the head (~44–47% y). Fallback
 * covers any other on-subject prop.
 */
export const ON_SUBJECT_ANCHOR_FALLBACK = { anchorXMilli: 505, anchorYMilli: 500 };

const ON_SUBJECT_ANCHORS: Record<string, { anchorXMilli: number; anchorYMilli: number }> = {
  // Shades sit low, over the eyes; hats/crown ride just above the head.
  // Eye line verified against the reel footage at ~60% y (see fx2 screenshots).
  "sticker-sunglasses": { anchorXMilli: 505, anchorYMilli: 610 },
  "sticker-tophat": { anchorXMilli: 505, anchorYMilli: 445 },
  "sticker-partyhat": { anchorXMilli: 545, anchorYMilli: 450 },
  "sticker-crown": { anchorXMilli: 505, anchorYMilli: 460 },
};

/** On-subject anchor for a sticker id, or null when the sticker is not a prop. */
export function onSubjectAnchor(
  stickerId: string,
  tags: readonly string[] = [],
): { anchorXMilli: number; anchorYMilli: number } | null {
  if (!tags.includes(ON_SUBJECT_TAG)) return null;
  return ON_SUBJECT_ANCHORS[stickerId] ?? ON_SUBJECT_ANCHOR_FALLBACK;
}
